import { omit } from "lodash";
import { AllowedBlockchains } from "../../../store/chain/models/internal";
import {
  AddressAttachmentType,
  countries,
  IdentityAttachmentType,
} from "../../common/models";
import { LaunchConfigurationsDTO } from "../../launch-configurations/models/dto";
import { StatusEnum } from "../../users/models/internal";

export interface Launch {
  between: {
    start: string;
    end: string;
  };
  description: string;
  id: string;
  imageUrl: string;
  initialTokenPrice?: number;
  plannedRaiseAmount?: number;
  name: string;
  pageUrl: string;
  pledgingInfo?: Record<string, LaunchConfigurationsDTO.PledgingInfo.T>;
  urls: {
    Homepage?: string;
    Medium?: string;
    Whitepaper?: string;
    Telegram?: string;
    Twitter?: string;
    Facebook?: string;
  };
}

export namespace LaunchesDTO {
  export namespace Requests {
    export namespace ParticipationForm {
      export interface T {
        user: {
          emailAddress: string;
          names: {
            first: string;
            last: string;
            middle?: string;
          };
          applicationPin: string;
          dateOfBirth: Date;
          nationality: typeof countries.T;
          countryOfResidence: typeof countries.T;
          physicallyLocatedIn: typeof countries.T;
          address: {
            addressLine1: string;
            addressLine2: string;
            city: string;
            zip: string;
            state: string;
            country: typeof countries.T;
          };
          locale: string;
          alreadyProcessed: boolean;
        };

        customQuestions?: Array<{
          fieldName: string;
          answer: any;
        }>;

        policies: {
          sekuritanceStatements: boolean;
          finalDeclaration: boolean;
          declarationStatements: boolean;
          whitepaperRead: boolean;
        };

        regulations: {
          pep: boolean;
          resideSanctionedCountry: boolean;
          usPerson: boolean;
        };

        pledge?: {
          amount: number;
          currency: string;
          chain: AllowedBlockchains;
        };

        sourceWallet?: {
          address: string;
          chain: AllowedBlockchains;
        };

        destinationWallet?: {
          address: string;
          chain: AllowedBlockchains;
        };

        stakingWallet?: {
          address: string;
          chain: AllowedBlockchains;
        };

        identityDocument: {
          attachment: {
            file: File | null;
            type: IdentityAttachmentType;
          };
          additionalFile?: File | null;
          details: {
            number: string;
            issueDate: Date;
            expiryDate: Date;
          };
        };

        addressAttachment?: {
          file: File | null;
          type: AddressAttachmentType;
        };

        faceAttachment?: File | null;
        carbonConsentSelfie?: FileList;
        captchaToken: string;
        device: {
          ipAddress: string;
          ipCountryCode: string;
        };
      }

      export type ForForm = Omit<T, "customQuestions"> & {
        custom?: Record<string, any>;
      };
    }

    export namespace ParticipationFormActual {
      /**
       * This mapper will transform the form data to the actual DTO
       * This is because the form does not match the actual DTO
       * e.g. firstName and lastName are in the form as 1 object due to the
       * form configuration provided by the backend
       */
      export function mapToActualDTO(formData: ParticipationForm.T): T {
        return {
          ...formData,
          user: {
            ...omit(formData.user, "names", "dateOfBirth"),
            firstName: formData.user.names?.first,
            lastName: formData.user.names?.last,
            middleName: formData.user.names?.middle,
            dob: formData.user.dateOfBirth,
            applicationPin: formData.user.applicationPin
          },
        };
      }

      export type T = Omit<ParticipationForm.T, "user"> & {
        user: Omit<ParticipationForm.T["user"], "names" | "dateOfBirth"> & {
          firstName: string;
          lastName: string;
          middleName?: string;
          dob: Date;
        };
      };
    }
  }

  export namespace Responses {
    export namespace ParticipationForm {
      export type T = {
        // TODO
      };
    }
    export namespace GetSingle {
      export type T = Launch;
    }
    export namespace GetAll {
      export type T = {
        launches: {
          ACTIVE?: Array<Launch>;
          PAST?: Array<Launch>;
          UPCOMING?: Array<Launch>;
        };
      };

      export const defaults: T = {
        launches: {
          ACTIVE: [],
          PAST: [],
          UPCOMING: [],
        },
      };
    }

    export namespace VerifyOtp {
      export type T = {
        status: typeof StatusEnum.T;
        success: boolean;
        userExists: boolean;
      };
    }
    export namespace CheckPreviousParticipation {
      export type T = {
        previousApplicationDate: Date | null;
        previousApplicationFound: boolean;
      };
    }
  }
}
